import { useState } from 'react';

import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getSelectedOfferVariation } from '@helpers/FormalOffers/getSelectedOfferVariation';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    fetchGetFormalOffers,
    fetchPutFormalOffersCreditFacilityConfiguration
} from '@iwoca/lapi-client/edge';
import { Button, CheckBox, Label, Text } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { useStateKey } from '../../../../../../iwQueryClient/useStateKey';
import { isSelectedAmountValid } from '../../../../../ui/ChooseAmountInputWithSlider/isSelectedAmountValid';
import { OfferVariationModal } from '../../../../approvedView/offerVariationModal/OfferVariationModal';

export const ContinueButton = ({
    selectedOffer,
    drawdownAmount
}: {
    selectedOffer: TFormalOffer | undefined;
    drawdownAmount: number | undefined;
}) => {
    const history = useHistory();
    const intl = useIntl();
    const stateKey = useStateKey();
    const queryClient = useQueryClient();
    const [isAffordabilityChecked, setIsAffordabilityChecked] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isVariationModalOpen, setIsVariationModalOpen] = useState(false);

    const affordabilitySplitTest = useSplitTestBranch({
        test: 'gpe_affordability_lending_test_live',
        suspense: false
    });

    const interestOnlySplitTest = useSplitTestBranch({
        test: 'new_customer_interest_only_add_on_test_2025_q2',
        suspense: false
    });

    const {
        data: formalOffersResponse,
        isLoading: isFormalOffersLoading,
        isError: isFormalOffersError
    } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey })
    });

    const isLoading = isFormalOffersLoading;
    const isError = isFormalOffersError;

    const formalOffers = formalOffersResponse?.data;

    const offerVariation = getSelectedOfferVariation(
        formalOffers?.formal_offers ?? [],
        formalOffers?.formal_offer_variation_relationship_trees ?? [],
        selectedOffer
    );

    const shouldShowInterstitialPage =
        interestOnlySplitTest === 'INTERSTITIAL_PAGE';

    const selectedOfferHasVariation =
        !!selectedOffer && !!offerVariation && !!drawdownAmount;

    const isOnAffordabilitySplitTest = affordabilitySplitTest === 'INCLUDE';

    const handleContinue = () => {
        if (isError) {
            setErrorMessage(
                intl.formatMessage({
                    id: '_.compare_offers_table.error',
                    defaultMessage:
                        'Something went wrong. Please try again later.'
                })
            );
            return;
        }

        if (!selectedOffer) {
            setErrorMessage(
                intl.formatMessage({
                    id: '_.loan_summary.error_no_offer',
                    defaultMessage: 'Please select an offer'
                })
            );
            return;
        }

        if (
            !drawdownAmount ||
            !isSelectedAmountValid(drawdownAmount, selectedOffer)
        ) {
            setErrorMessage(
                intl.formatMessage({
                    id: '_.loan_summary.error_no_amount',
                    defaultMessage:
                        'Please enter a valid drawdown amount to continue'
                })
            );
            return;
        }

        if (isOnAffordabilitySplitTest && !isAffordabilityChecked) {
            setErrorMessage(
                intl.formatMessage({
                    id: '_.loan_summary.error_affordability',
                    defaultMessage:
                        'Please confirm the repayment statement above to continue'
                })
            );
            return;
        }

        setErrorMessage(null);

        tracking.ampli.offersContinueCtaClicked();

        if (isAffordabilityChecked) {
            tracking.ampli.affordabilityCheckboxConfirmed();
        }

        if (selectedOfferHasVariation && shouldShowInterstitialPage) {
            setIsVariationModalOpen(true);
        } else {
            history.push(
                maintainStateKey({
                    pathname: '/funding-requirements/',
                    search: `?offerId=${selectedOffer!.offer_id}&amount=${drawdownAmount}`
                })
            );
        }
    };

    const handleVariationSubmit = async ({
        selectedOffer: chosenOffer
    }: {
        selectedOffer: TFormalOffer;
    }) => {
        await fetchPutFormalOffersCreditFacilityConfiguration({
            stateKey,
            offerId: chosenOffer.offer_id!,
            body: { data: { cash_amount: drawdownAmount! } }
        });

        await queryClient.invalidateQueries([
            'fetchGetFormalOffersLatestOfferConfiguration',
            stateKey
        ]);

        history.push(
            maintainStateKey({
                pathname: '/funding-requirements/',
                search: `?offerId=${chosenOffer.offer_id}&amount=${drawdownAmount}`
            })
        );
    };

    return (
        <div>
            {isOnAffordabilitySplitTest && (
                <div
                    className="mb-m flex items-start gap-l"
                    data-testid="affordability-checkbox"
                >
                    <CheckBox
                        id="affordability-checkbox-input"
                        checked={isAffordabilityChecked}
                        onChange={(e) => {
                            setIsAffordabilityChecked(e.currentTarget.checked);
                            setErrorMessage(null);
                        }}
                    />
                    <Label
                        className="mb-m cursor-pointer text-left text-s font-reg text-structure-100"
                        htmlFor="affordability-checkbox-input"
                    >
                        <FormattedMessage
                            id="_.offer_view.affordability_disclaimer"
                            defaultMessage="I acknowledge that I will have to repay the loan and have a plan which allows me to ensure I can afford to make the repayments."
                        />
                    </Label>
                </div>
            )}

            {errorMessage && (
                <Text className="mb-m text-center text-s font-bold text-error">
                    {errorMessage}
                </Text>
            )}

            <Button
                className={`w-full rounded-full text-primary-10 ${isLoading ? 'opacity-50' : ''}`}
                variant="secondary-alt"
                onClick={handleContinue}
                disabled={isLoading}
                data-testid="continue-to-final-checks"
            >
                <FormattedMessage
                    id="_.loan_summary.continue_button"
                    defaultMessage="Continue to final checks"
                />
            </Button>

            <Text className="mt-m text-center text-xs text-secondary-80">
                <FormattedMessage
                    id="_.loan_summary.continue_helper_text_v21"
                    defaultMessage='Before we can send funds to your account, we need you to complete a few final steps. Click "Continue" to get started.'
                />
            </Text>

            {selectedOfferHasVariation && (
                <OfferVariationModal
                    isOpen={isVariationModalOpen}
                    onClose={() => setIsVariationModalOpen(false)}
                    drawdownAmount={drawdownAmount}
                    baseOffer={selectedOffer}
                    variedOffer={offerVariation.variedOffer}
                    variationType={offerVariation.variationType}
                    handleSubmit={handleVariationSubmit}
                />
            )}
        </div>
    );
};
